/**
 * Palete predefinite + generator dinamic pentru tema aplicației.
 * Scale-urile sunt stocate ca hex-uri; se convertesc la RGB triplet la aplicare.
 */

export type Shade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
export type ColorScale = Record<Shade, string>;

export interface PalettePreset {
  id: string;
  name: string;
  base: string;
  scale: ColorScale;
}

export const PALETTE_PRESETS: Record<string, PalettePreset> = {
  'orange-sf': {
    id: 'orange-sf',
    name: 'Portocaliu SoftFactura',
    base: '#F15A24',
    scale: {
      50: '#FFF4EF', 100: '#FFE4D6', 200: '#FFC4A8', 300: '#FF9E74',
      400: '#F87940', 500: '#F15A24', 600: '#D84A18', 700: '#B03B12',
      800: '#82280B', 900: '#541806',
    },
  },
  'blue-royal': {
    id: 'blue-royal',
    name: 'Albastru regal',
    base: '#2E86C1',
    scale: {
      50: '#EBF5FB', 100: '#D4E9F7', 200: '#A9D2EE', 300: '#7FBCE5',
      400: '#54A6DC', 500: '#2E86C1', 600: '#2874A6', 700: '#21618C',
      800: '#1B4F72', 900: '#154360',
    },
  },
  'green-emerald': {
    id: 'green-emerald',
    name: 'Verde smarald',
    base: '#27AE60',
    scale: {
      50: '#E8F8F0', 100: '#D1F2E1', 200: '#A3E4C4', 300: '#76D7A6',
      400: '#48C989', 500: '#27AE60', 600: '#229954', 700: '#1E8449',
      800: '#186A3B', 900: '#10492A',
    },
  },
  'purple-wine': {
    id: 'purple-wine',
    name: 'Mov vinificat',
    base: '#8E44AD',
    scale: {
      50: '#F4ECF7', 100: '#E8DAEF', 200: '#D2B4DE', 300: '#BB8FCE',
      400: '#A569BD', 500: '#8E44AD', 600: '#7D3C98', 700: '#6C3483',
      800: '#5B2C6F', 900: '#4A235A',
    },
  },
  'teal-ocean': {
    id: 'teal-ocean',
    name: 'Teal ocean',
    base: '#148F77',
    scale: {
      50: '#E8F8F5', 100: '#D1F2EB', 200: '#A3E4D7', 300: '#76D7C4',
      400: '#48C9B0', 500: '#148F77', 600: '#117A65', 700: '#0E6655',
      800: '#0B5345', 900: '#073F35',
    },
  },
  'red-fire': {
    id: 'red-fire',
    name: 'Roșu foc',
    base: '#E74C3C',
    scale: {
      50: '#FDEDEC', 100: '#FADBD8', 200: '#F5B7B1', 300: '#F1948A',
      400: '#EC7063', 500: '#E74C3C', 600: '#CB4335', 700: '#A93226',
      800: '#7B241C', 900: '#52170F',
    },
  },
  'pink-rose': {
    id: 'pink-rose',
    name: 'Roz aurora',
    base: '#E91E63',
    scale: {
      50: '#FCE4EC', 100: '#F8BBD0', 200: '#F48FB1', 300: '#F06292',
      400: '#EC407A', 500: '#E91E63', 600: '#D81B60', 700: '#C2185B',
      800: '#AD1457', 900: '#880E4F',
    },
  },
  'neutral-slate': {
    id: 'neutral-slate',
    name: 'Ardezie neutră',
    base: '#475569',
    scale: {
      50: '#F8FAFC', 100: '#F1F5F9', 200: '#E2E8F0', 300: '#CBD5E1',
      400: '#94A3B8', 500: '#475569', 600: '#334155', 700: '#1E293B',
      800: '#0F172A', 900: '#020617',
    },
  },
  'indigo-discord': {
    id: 'indigo-discord',
    name: 'Indigo',
    base: '#5865F2',
    scale: {
      50: '#EEF0FE', 100: '#DDE1FD', 200: '#BBC3FB', 300: '#94A1F8',
      400: '#7180F5', 500: '#5865F2', 600: '#404DD8', 700: '#2F3AA8',
      800: '#1F2778', 900: '#121752',
    },
  },
  'blue-cobalt': {
    id: 'blue-cobalt',
    name: 'Cobalt',
    base: '#0061FE',
    scale: {
      50: '#E5EFFF', 100: '#CCE0FF', 200: '#99C0FF', 300: '#66A1FF',
      400: '#3381FF', 500: '#0061FE', 600: '#004FCC', 700: '#003C99',
      800: '#002966', 900: '#001833',
    },
  },
  'green-action': {
    id: 'green-action',
    name: 'Verde acțiune',
    base: '#2DA44E',
    scale: {
      50: '#E8F5EC', 100: '#C9E8D2', 200: '#93D2A6', 300: '#5DBC79',
      400: '#3FB264', 500: '#2DA44E', 600: '#258940', 700: '#1D6D33',
      800: '#155226', 900: '#0E3719',
    },
  },
  'mono': {
    id: 'mono',
    name: 'Curat (monocrom)',
    base: '#525252',
    scale: {
      50: '#FAFAFA', 100: '#F5F5F5', 200: '#E5E5E5', 300: '#D4D4D4',
      400: '#A3A3A3', 500: '#737373', 600: '#525252', 700: '#404040',
      800: '#262626', 900: '#171717',
    },
  },
};

export const DEFAULT_PRESET_ID = 'orange-sf';

// ═══════════════════════════════════════════════════════════
//  hex ↔ rgb helpers
// ═══════════════════════════════════════════════════════════

export function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '').padEnd(6, '0');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

export function hexToRgbTriplet(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

// ═══════════════════════════════════════════════════════════
//  HSL conversions (for scale generation)
// ═══════════════════════════════════════════════════════════

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360; s /= 100; l /= 100;
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function rgbToHex(r: number, g: number, b: number): string {
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`.toUpperCase();
}

// ═══════════════════════════════════════════════════════════
//  Luminance + contrast helpers (WCAG 2.1 relative luminance)
// ═══════════════════════════════════════════════════════════

export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  const rs = r / 255, gs = g / 255, bs = b / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(rs) + 0.7152 * lin(gs) + 0.0722 * lin(bs);
}

export function contrastRatio(hexA: string, hexB: string): number {
  const lA = luminance(hexA);
  const lB = luminance(hexB);
  const lighter = Math.max(lA, lB);
  const darker = Math.min(lA, lB);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Întoarce hex-ul primit sau unul întunecat dacă e prea deschis.
 * Asigură că textul alb are contrast suficient (~4.5:1, WCAG AA) pe fundal primar.
 */
export function ensureUsable(hex: string): { hex: string; adjusted: boolean; originalHex: string } {
  const original = hex.startsWith('#') ? hex.toUpperCase() : '#' + hex.toUpperCase();
  const ratio = contrastRatio(original, '#FFFFFF');

  // Dacă contrastul cu alb e ≥ 3.5 → OK pentru majoritatea UI (butoane, badge-uri)
  if (ratio >= 3.5) {
    return { hex: original, adjusted: false, originalHex: original };
  }

  // Culoare prea deschisă → întunecăm menținând hue + saturation
  const [r, g, b] = hexToRgb(original);
  const [h, s] = rgbToHsl(r, g, b);
  // Căutăm L care produce contrast cu alb ≥ 4.5. Binary search rapid.
  let lo = 0, hi = 100, best = 40;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    const [rr, gg, bb] = hslToRgb(h, Math.max(s, 30), mid);
    const candidateHex = rgbToHex(rr, gg, bb);
    const cr = contrastRatio(candidateHex, '#FFFFFF');
    if (cr >= 4.5) { best = mid; lo = mid; } else { hi = mid; }
  }
  const [fr, fg, fb] = hslToRgb(h, Math.max(s, 30), best);
  return { hex: rgbToHex(fr, fg, fb), adjusted: true, originalHex: original };
}

// ═══════════════════════════════════════════════════════════
//  Scale generator from a single base hex
//  Strategy: preserve hue + saturation, vary lightness per shade
// ═══════════════════════════════════════════════════════════

// Target lightness values per shade. Tuned so the 500 value keeps the user's
// chosen hex lightness (interpolated), while 50 stays very light and 900
// stays very dark — mirrors Tailwind conventions.
const SHADE_LIGHTNESS_OFFSETS: Record<Shade, number> = {
  50: 50, 100: 42, 200: 30, 300: 18, 400: 8,
  500: 0, 600: -8, 700: -18, 800: -28, 900: -38,
};

export function generateScale(baseHex: string): ColorScale {
  const [r, g, b] = hexToRgb(baseHex);
  const [h, s, l] = rgbToHsl(r, g, b);

  const shades: Shade[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
  const scale = {} as ColorScale;

  for (const shade of shades) {
    const targetL = Math.max(4, Math.min(97, l + SHADE_LIGHTNESS_OFFSETS[shade]));
    // Slightly desaturate extreme light shades for better look
    const targetS = shade <= 100 ? Math.min(s, 85) : s;
    const [rr, gg, bb] = hslToRgb(h, targetS, targetL);
    scale[shade] = rgbToHex(rr, gg, bb);
  }

  // Force the 500 shade to exactly match user's input (no drift)
  scale[500] = baseHex.toUpperCase().startsWith('#') ? baseHex.toUpperCase() : `#${baseHex.toUpperCase()}`;
  return scale;
}

export type BgTone = 'auto' | 'tinted-subtle' | 'tinted-medium' | 'neutral' | 'warm';

export const BG_TONE_OPTIONS: Array<{ id: BgTone; label: string; description: string }> = [
  { id: 'auto', label: 'Automat', description: 'Se adaptează la paleta aleasă (recomandat)' },
  { id: 'tinted-subtle', label: 'Subtil din paletă', description: 'Nuanță foarte deschisă, tint mic' },
  { id: 'tinted-medium', label: 'Vizibil din paletă', description: 'Nuanța 50 din paletă, tint clar' },
  { id: 'neutral', label: 'Neutru gri', description: '#F4F4F3 — stone neutru, independent de paletă' },
  { id: 'warm', label: 'Cald crem', description: '#F5F2EB — crem clasic SoftFactura' },
];

/**
 * Mix a hex with white at a given ratio (0 = only hex, 1 = only white).
 */
function mixWithWhite(hex: string, whiteRatio: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const mix = (c: number) => Math.round(c * (1 - whiteRatio) + 255 * whiteRatio);
  const toHex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
  return '#' + toHex(mix(r)) + toHex(mix(g)) + toHex(mix(b));
}

/**
 * Returnează culoarea potrivită pentru --surface-muted în funcție de tone
 * și paleta curentă. Folosit de ThemeApplier pentru a derive fundalul
 * secundar (hover, header tabel, chip-uri inactive etc).
 */
export function getSurfaceMutedHex(scale: ColorScale, tone: BgTone): string {
  switch (tone) {
    case 'neutral':
      return '#F4F4F3';
    case 'warm':
      return '#F5F2EB';
    case 'tinted-medium':
      return scale[50];
    case 'tinted-subtle':
      return mixWithWhite(scale[50], 0.55);
    case 'auto':
    default:
      return mixWithWhite(scale[50], 0.7);
  }
}
